// 生图模块枚举
import {
  module,
  ModuleEnum,
  INTELLIGENT_EDIT_V2_MODEL_ID,
  VISUAL_EXPANSION_V2_MODEL_ID,
  REPAINT_UPSCALE_MODULE_ID,
} from "./moduleEnum";

// 默认值
import {
  useReferenceColorWeight,
  notUseReferenceColorWeight,
  poseWeight,
  fluxWeight,
  redrawWeightDefault,
  redrawWeightMin,
  redrawWeightMax,
  defaultBatchSize,
  defaultAspectRatios,
  defaultModelId,
  defaultFluxModelId
} from "./default";
import { expansionParams } from "./module/expansion";
import { ModuleId, ModuleKeys } from "@xmiles/holopix-types";

// 下拉选项（提前导入，避免循环依赖）
import {
  sourceOptions,
  moduleOptions,
  numberOptions,
  canvasModuleOptions,
  FREE_PERSPECTIVE_MODULE_IDS,
  getArtworkModuleOptionLabel,
} from "./options";

// 非智能编辑模块初始参数
import { exploreParams } from "./module/explore";
import { colourParams } from "./module/colour";
import { refineParams } from "./module/refine";
import { sketchParams } from "./module/sketch";
import { fissionParams } from "./module/fission";
import { threeTransformParams } from "./module/threeTransform";
import { mattingParams } from "./module/matting";
import { replaceParams } from "./module/replace";
import { zoomParams } from "./module/zoom";
import { pixelateParams } from "./module/pixelate";
import { aiSeedanceParams } from "./module/aiSeedance";
import { expressionParams, startExpressionGenerate } from "./module/expression";
import { renderParams } from "./module/render";
import { freePerspectiveParams, startFreePerspectiveGenerate } from "./module/freePerspective";
import { styleRenderDebugParams, startStyleRenderDebugGenerate } from "./module/styleRenderDebug";

// 各非智能编辑模块生图
import type { Module } from "@/types/artwork/generate";
import { startExploreGenerate } from "./module/explore";
import { startColourGenerate } from "./module/colour";
import { startRefineGenerate } from "./module/refine";
import { startSketchGenerate } from "./module/sketch";
import { startFissionGenerate } from "./module/fission";
import { startThreeTransformGenerate } from "./module/threeTransform";
import { startReplaceGenerate } from "./module/replace";
import { startMattingGenerate } from "./module/matting";
import { startZoomGenerate } from "./module/zoom";
import { startZoomRedrawGenerate } from "./module/zoomRedraw";
import { startPixelateGenerate } from "./module/pixelate";
import { startAivideoGenerate } from "./module/aivideo";
import { startRenderGenerate } from "./module/render";
import { startExpansionGenerate } from "./module/expansion";

import { styleTypeOptions, styleTypeOptionsA1 } from "./module/explore";

export {
  module,
  ModuleEnum,
  VISUAL_EXPANSION_V2_MODEL_ID,
  INTELLIGENT_EDIT_V2_MODEL_ID,
  resolveStoreModuleId,
  isExpansionModuleId,
  isReplaceModuleId,
  resolveVersionListQueryModule
} from "./moduleEnum";

export const initParams = {
  explore: exploreParams,
  colour: colourParams,
  refine: refineParams,
  sketch: sketchParams,
  fission: fissionParams,
  threeTransform: threeTransformParams,
  matting: mattingParams,
  replace: replaceParams,
  zoom: zoomParams,
  expansion: expansionParams,
  pixelate: pixelateParams,
  aivideo: aiSeedanceParams,
  expression: expressionParams,
  render: renderParams,
  freePerspective: freePerspectiveParams,
  styleRenderTrain: styleRenderDebugParams,
} as const as Record<ModuleKeys, unknown>;

export { exploreParams, colourParams, refineParams, replaceParams, mattingParams, zoomParams, renderParams };

// 开启生图逻辑
export function startGenerate(
  module: number,
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  if (module === VISUAL_EXPANSION_V2_MODEL_ID) {
    startExpansionGenerate(oldParams, options);
    return;
  }
  if (module === INTELLIGENT_EDIT_V2_MODEL_ID) {
    startReplaceGenerate(oldParams, options);
    return;
  }
  // 重绘放大 (module=59) 需要单独处理，不应该被归一化为 zoom (20)
  if (module === ModuleId.RepaintUpscale) {
    startZoomRedrawGenerate(oldParams, options);
    return;
  }

  switch (module) {
    case ModuleId.Explore:
      startExploreGenerate(oldParams, options);
      break;
    case ModuleId.Colour:
      startColourGenerate(oldParams, options);
      break;
    case ModuleId.Refine:
      startRefineGenerate(oldParams, options);
      break;
    case ModuleId.Sketch:
      startSketchGenerate(oldParams, options);
      break;
    case ModuleId.Fission:
      startFissionGenerate(oldParams, options);
      break;
    case ModuleId.ThreeTransform:
      startThreeTransformGenerate(oldParams, options);
      break;
    case ModuleId.Replace:
      startReplaceGenerate(oldParams, options);
      break;
    case ModuleId.Matting:
      startMattingGenerate(oldParams);
      break;
    case ModuleId.Zoom:
      startZoomGenerate(oldParams);
      break;
    case ModuleId.Expansion:
      startExpansionGenerate(oldParams, options);
      break;
    case ModuleId.RepaintUpscale:
      startZoomRedrawGenerate(oldParams);
      break;
    case ModuleId.Pixelate:
      startPixelateGenerate(oldParams);
      break;
    case ModuleId.Aivideo:
      startAivideoGenerate(oldParams);
      break;
    case ModuleId.Expression:
      startExpressionGenerate(oldParams);
      break;
    case ModuleId.Render:
      startRenderGenerate(oldParams, options);
      break;
    case ModuleId.FreePerspective:
    case ModuleId.QwenFreePerspective:
      startFreePerspectiveGenerate(oldParams, options);
      break;
    case ModuleId.StyleRenderTrain:
      startStyleRenderDebugGenerate(oldParams, options);
      break;
  }
}

export {
  sourceOptions,
  moduleOptions,
  canvasModuleOptions,
  FREE_PERSPECTIVE_MODULE_IDS,
  styleTypeOptions,
  styleTypeOptionsA1,
  numberOptions,
  getArtworkModuleOptionLabel
};

// 作品模型需要用到模型详情里面的字段
export const artworkUseModelField = [
  "id",
  "category",
  "ckptType",
  "loraType",
  "loraList",
  "strength",
  "maxStrength",
  "minStrength",
  "modelName",
  "preview",
  "prompt",
  "ratio",
  "faceDetail",
  "imageGuidanceWeights",
  "aspectRatios"
];

export {
  useReferenceColorWeight,
  notUseReferenceColorWeight,
  poseWeight,
  fluxWeight,
  redrawWeightDefault,
  redrawWeightMin,
  redrawWeightMax,
  defaultBatchSize,
  defaultAspectRatios,
  defaultModelId,
  defaultFluxModelId
};

// 生图埋点中的模型分类
export function getModelEventTrackType(modelList: any[] = []): number {
  const length = modelList.length;
  let initType: number | undefined = undefined;
  let isMix = false; // 是否混合

  // 使用分享模型
  if (modelList.every((item) => item.parentId)) {
    return 5;
  }

  // 使用了团队共享模式
  if (modelList.every((item) => item.teamId && item.shareType === 1)) {
    return 4;
  }

  for (let i = 0; i < length; i++) {
    if (initType === undefined) {
      initType = modelList[i].source;
    } else if (initType !== modelList[i].source) {
      isMix = true;
      break;
    }
  }

  if (isMix) {
    // 混合模型
    return 2;
  }

  if (initType === 2) {
    // 全部为组合模型
    return 3;
  }

  return (initType as number) || 0;
}

export type ModelSource = "official" | "svip_custom" | "community_share" | "team_share";

export function mapModelSource(trackType: number): ModelSource {
  const mapping: Record<number, ModelSource> = {
    0: "official",
    1: "official",
    2: "official",
    3: "svip_custom",
    4: "team_share",
    5: "community_share"
  };
  return mapping[trackType] ?? "official";
}

// 生图埋点中的形象样式参考（styleReferType — 1: 默认 2: 构图 3: 风格 4: 脸部）
export function getStyleRefer(styleReferType: number, imageList?: any[]) {
  if (imageList && imageList.length > 0) {
    switch (styleReferType) {
      case 1:
        return "默认";
      case 2:
        return "构图";
      case 3:
        return "风格";
      case 4:
        return "脸部";
    }
  }
}

// 生图埋点中的高清修复
export function gethdFix(enable: boolean, hdScale: number) {
  if (enable) {
    return `${hdScale}x`;
  }
}
